import { LinkButton, PageTitle } from '@sleepstation/components'
import { BreakingLayout, HLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_CHILD,
  LAYOUT_JUSTIFY,
  LAYOUT_SPACING,
} from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  mq,
  paddingBottom,
  paddingTop,
  propToExplicitMaxHeight,
  propToExplicitMaxWidth,
  propToTop,
  top,
} from '@sleepstation/style-api-helpers'
import { constrainWide } from '@sleepstation/style-mixins'
import { defaultLightestTheme } from '@sleepstation/themes-public-site'
import { rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { map } from 'ramda'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import HeroProductShot from '../../HeroProductShot'
import Footer from './Footer/Footer'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const LOGO_WIDTHS = map(rem, [280, 460, 300, 460])

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(BreakingLayout).attrs({
  direction: DIRECTION.VERTICAL,
  justify: LAYOUT_JUSTIFY.CENTER,
  align: LAYOUT_ALIGN.CENTER,
  spacing: [LAYOUT_SPACING.X_LARGE, 3, 5],
})`
  position: relative;
  overflow: hidden;
  ${paddingTop([3.5, 5.5, 6])}
`

const Title = styled(PageTitle)`
  ${mq(`mediumDown`)} {
    align-self: stretch;
  }
`

const LogoWrapper = styled(BreakingLayout).attrs({
  direction: DIRECTION.VERTICAL,
})`
  width: 100%;

  > * {
    display: block;

    ${propToExplicitMaxWidth(`widths`)}
    ${propToExplicitMaxHeight(`heights`)}

    ${mq(`xSmallOnly`)} {
      margin: 0 auto;
    }
  }
`

const Primary = styled(BreakingLayout).attrs({
  direction: DIRECTION.VERTICAL,
  align: [LAYOUT_ALIGN.CENTER, LAYOUT_ALIGN.CENTER, LAYOUT_ALIGN.LEFT],
  spacing: LAYOUT_SPACING.LARGE,
})`
  width: 100%;
  max-width: none;

  ${mq(`xSmallOnly`)} {
    ${paddingBottom(0.5)}
  }
`

const Secondary = styled.div`
  max-width: ${rem(586)};
  position: relative;
  ${top(0.5)}

  ${mq(`mediumDown`)} {
    display: none;
  }
`

const ButtonWrapper = styled.div`
  width: 66.6%;

  > * {
    width: 100%;
  }

  ${mq(`mediumDown`)} {
    width: 100%;
  }
`

const Body = styled(HLayout).attrs({
  child: LAYOUT_CHILD.EQUAL,
  spacing: LAYOUT_SPACING.X_LARGE,
})`
  flex: 1 1 auto;
  ${constrainWide()}
`

const LogoOuter = styled.div`
  ${paddingTop([0.5])}
  position: relative;
  width: 100%;
  ${propToTop(`offsetTop`)}
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const CorporateAccessHero = ({
  title,
  body,
  ctaLabel,
  logo,
  ctaUrl,
  productShot,
  logoHeights,
  logoOffsetsTop,
  logoAlignments,
}) => {
  const LOGO_HEIGHTS = map(rem, logoHeights)
  const bodyAst = parseToAst(body)

  return (
    <Layout>
      <Body>
        <Primary>
          <Title>{title}</Title>
          <CopyRenderer htmlAst={bodyAst} />
          <ButtonWrapper>
            <LinkButton
              href={ctaUrl}
              data-cy="start-now-cta-link"
              data-tr-cta="home-header"
              forceOpenInSameWindow
            >
              {ctaLabel}
            </LinkButton>
          </ButtonWrapper>
          <LogoOuter offsetTop={logoOffsetsTop}>
            <LogoWrapper
              widths={LOGO_WIDTHS}
              heights={LOGO_HEIGHTS}
              align={logoAlignments}
            >
              <img alt={logo.alt} src={logo.url} />
            </LogoWrapper>
          </LogoOuter>
        </Primary>
        <Secondary>
          <HeroProductShot image={productShot} />
        </Secondary>
      </Body>
      <ThemeProvider theme={defaultLightestTheme}>
        <Footer productShot={productShot} />
      </ThemeProvider>
    </Layout>
  )
}

CorporateAccessHero.propTypes = {
  title: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  ctaUrl: PropTypes.string.isRequired,
  productShot: PropTypes.object.isRequired,
  body: PropTypes.string.isRequired,
  logo: PropTypes.object.isRequired,
  logoHeights: PropTypes.arrayOf(PropTypes.number),
  logoOffsetsTop: PropTypes.arrayOf(PropTypes.number),
  logoAlignments: PropTypes.arrayOf(PropTypes.string),
}

CorporateAccessHero.defaultProps = {
  logoHeights: [70, 90, 90, 90],
  logoAlignments: [LAYOUT_ALIGN.CENTER, LAYOUT_ALIGN.LEFT],
  logoOffsetsTop: [0],
}

export default CorporateAccessHero
